import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, Observable } from 'rxjs';
import { FeedApiService } from 'src/app/api/services/feed-api.service';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import {
  searchPosts,
  toggleAdvancedSearch,
} from 'src/app/core/actions/feed.actions';
import {
  selectAdvancedSearchOpen,
  selectFeed,
  selectUser,
} from 'src/app/core/reducers';
import {
  RecentSearchesScope,
  injectAddRecentSearchMutation,
  injectRecentSearchesQuery,
  injectRemoveRecentSearchMutation,
} from 'src/app/api/queries/user.queries';

import { AdvancedSearchService } from 'src/app/core/services/advanced-search.service';
import { AppState } from 'src/app/reducers';
import { RecentSearch } from 'src/app/core/models/core.models';
import { Store } from '@ngrx/store';
import { ToastService } from '@services/toast.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-advanced-quick-search',
  templateUrl: './advanced-quick-search.component.html',
  styleUrls: ['./advanced-quick-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AdvancedQuickSearchComponent implements OnInit {
  /**
   * Display text (already translated) for the placeholder. Forwarded as-is to
   * `app-search-input`; left unset, that component supplies the translated default.
   */
  @Input() placeholder?: string;
  @Input() debounceTime: number;

  /**
   * Feed mode (default) keeps the original behaviour: validate the query against
   * the current feed and dispatch `searchPosts`. Set false to reuse the search
   * box on a non-feed screen — `applySearch` then just emits `querySubmit`,
   * skipping the feed validate/search and the recent-search history.
   */
  @Input() feedMode: boolean = true;
  /** Hide the advanced (Solr query editor) toggle on screens without one. */
  @Input() showAdvancedToggle: boolean = true;
  /**
   * Hide the search field, leaving only the advanced toggle. Used by collapsed
   * toolbars that keep the toggle reachable when the field is squeezed out.
   */
  @Input() showSearchField: boolean = true;
  /** Emitted on submit when feedMode is false. */
  @Output() querySubmit = new EventEmitter<string>();

  private _value = '';
  @Input() set value(v: string) {
    this._value = v ?? '';
    this.currentQuery = this._value;
    this.updateFilteredSearches();
  }
  get value() {
    return this._value;
  }

  advancedSearchOpen$: Observable<boolean>;

  // raw recent list from store (kept for id removal)
  private recentSearches: RecentSearch[] = [];

  currentQuery: string = '';
  historyListOpen: boolean = false;
  filteredRecentSearches: RecentSearch[] = [];

  // keyboard nav
  activeIndex: number = -1;

  // refs for autoscroll
  @ViewChildren('historyItemRef') historyItemRefs!: QueryList<
    ElementRef<HTMLElement>
  >;

  private destroyRef = inject(DestroyRef);
  private feedApi = inject(FeedApiService);
  private toastService = inject(ToastService);
  private advancedSearchService = inject(AdvancedSearchService);
  private readonly addRecentSearchMutation = injectAddRecentSearchMutation();
  private readonly removeRecentSearchMutation =
    injectRemoveRecentSearchMutation();

  private readonly user = inject(Store<AppState>).selectSignal(selectUser);

  /**
   * `feedMode` is an `@Input`, so it is not readable when the query is built. This
   * signal gates the fetch instead, and is set in `ngOnInit`.
   */
  private readonly historyEnabled = signal(false);

  private readonly scope = computed<RecentSearchesScope | null>(() => {
    const user = this.user();

    return this.historyEnabled() && user?.id
      ? { userId: user.id, companyId: user.active_company_id }
      : null;
  });

  private readonly recentSearchesQuery = injectRecentSearchesQuery(this.scope);

  // A field, not an `ngOnInit` local: `toObservable` needs an injection context.
  readonly recentSearches$: Observable<RecentSearch[]> = toObservable(
    computed(() => this.recentSearchesQuery.data() ?? []),
  );

  constructor(
    private eRef: ElementRef,
    private store: Store<AppState>,
  ) {
    this.advancedSearchOpen$ = this.store.select(selectAdvancedSearchOpen);
  }

  ngOnInit(): void {
    this.currentQuery = this.value ?? '';
    this.updateFilteredSearches();

    if (!this.feedMode) {
      // No feed context: skip recent-search history entirely
      return;
    }

    this.historyEnabled.set(true);

    this.recentSearches$
      .pipe(
        tap((items) => {
          this.recentSearches = items ?? [];
          this.updateFilteredSearches();
          // keep activeIndex in range when list changes
          const len = this.filteredRecentSearches.length;
          if (len === 0) {
            this.activeIndex = -1;
          } else if (this.activeIndex >= len) {
            this.activeIndex = len - 1;
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private updateFilteredSearches(): void {
    const q = String(this.currentQuery ?? '')
      .trim()
      .toLowerCase();

    if (!q) {
      this.filteredRecentSearches = this.recentSearches;
    } else {
      this.filteredRecentSearches = this.recentSearches.filter((item) =>
        String(item.query_text ?? '')
          .toLowerCase()
          .includes(q),
      );
    }
  }

  get filteredHistoryList(): string[] {
    return this.filteredRecentSearches.map((i) => i.query_text).filter(Boolean);
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: MouseEvent) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.closeHistory();
      this.activeIndex = -1;
    }
  }

  // Keyboard navigation
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!this.historyListOpen || this.filteredRecentSearches.length === 0) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.syncQueryFromInput(event);
        this.applySearch();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Enter':
        if (this.activeIndex >= 0) {
          event.preventDefault();
          const item = this.filteredRecentSearches[this.activeIndex];
          this.selectHistoryItem(item.query_text);
        } else {
          this.syncQueryFromInput(event);
          this.applySearch();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeHistory();
        break;
      case 'Delete':
        if (this.activeIndex >= 0) {
          event.preventDefault();
          const item = this.filteredRecentSearches[this.activeIndex];
          this.doRemoveRecent(item);
        }
        break;
    }
  }

  private moveActive(delta: number) {
    const len = this.filteredRecentSearches.length;
    if (len === 0) {
      this.activeIndex = -1;
      return;
    }
    if (this.activeIndex === -1) {
      this.activeIndex = delta > 0 ? 0 : len - 1;
    } else {
      this.activeIndex = (this.activeIndex + delta + len) % len;
    }
    this.scrollActiveIntoView();
  }

  private scrollActiveIntoView() {
    const el = this.historyItemRefs?.get(this.activeIndex)?.nativeElement;
    if (el) el.scrollIntoView({ block: 'nearest' });
  }

  private closeHistory() {
    this.historyListOpen = false;
    this.activeIndex = -1;
  }

  handleInputChange(value: string) {
    this.currentQuery = String(value ?? '');
    this.advancedSearchService.setQuery(this.currentQuery);
    this.updateFilteredSearches();
    this.activeIndex = -1;
  }

  /**
   * Capture the live input value on submit. The inner search-input debounces
   * its `inputChanged`, so on a quick type-then-Enter `currentQuery` can still
   * be stale; reading the field directly makes Enter apply what's on screen.
   */
  private syncQueryFromInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (target && typeof target.value === 'string') {
      this.currentQuery = target.value;
      this.advancedSearchService.setQuery(this.currentQuery);
    }
  }

  handleAdvancedOpened(_: any) {
    this.store.dispatch(toggleAdvancedSearch());
  }

  handleHistoryOpened(_: any) {
    this.historyListOpen = !this.historyListOpen;
    if (!this.historyListOpen) this.activeIndex = -1;
  }

  handleHistoryItemClick(item: RecentSearch, event?: MouseEvent) {
    if (event) event.stopPropagation();
    this.selectHistoryItem(item.query_text);
  }

  private selectHistoryItem(text: string) {
    this.advancedSearchService.setQuery(text);
    this.applySearch();
  }

  applySearch() {
    if (!this.feedMode) {
      // Non-feed screens own the search semantics — just hand them the query
      this.querySubmit.emit(this.currentQuery);
      this.closeHistory();
      return;
    }

    this.store
      .select(selectFeed)
      .pipe(
        take(1),
        switchMap((feed) =>
          this.feedApi
            .validateQuery(
              feed.object_type_id,
              this.currentQuery,
              'editor',
              feed.id,
            )
            .pipe(
              tap(() => {
                if (this.currentQuery?.length) {
                  this.addRecentSearchMutation.mutate({
                    query_text: this.currentQuery,
                  });
                }

                this.store.dispatch(searchPosts({ query: this.currentQuery }));

                this.closeHistory();
              }),
              catchError((err) => {
                this.toastService.showError(err?.message);
                return EMPTY;
              }),
            ),
        ),
      )
      .subscribe();
  }

  removeRecent(item: RecentSearch, event: MouseEvent) {
    event.stopPropagation();
    this.doRemoveRecent(item);
  }

  private doRemoveRecent(item: RecentSearch) {
    // Matched on its criteria: the backend returns no id to remove by.
    this.removeRecentSearchMutation.mutate({
      query_text: item.query_text,
      filters: item.filters ?? null,
      lang: item.lang ?? null,
      scope_id: item.scope_id ?? null,
    });
  }

  trackByRecent = (_: number, item: RecentSearch) => item.query_text;

  onFocusIn() {
    // Show all recent searches on focus
    this.historyListOpen = true;
    this.activeIndex = -1;
  }
}
